import cruisesAPI from './CruisesApiService';

export type CruiseIqQueryRequest = {
  query: string;
};

export type CruiseIqQueryResult = {
  explanation: string;
  rows: Array<Record<string, unknown>>;
  rowCount: number;
};

export type InclusionBreakdownRow = {
  vendorCode: string;
  inclusionType: string;
  bookingCount: number;
};

export type InclusionPerformanceRow = {
  inclusionId: string;
  inclusionName: string;
  bookingCount: number;
  averageMarginPercent: number | null;
  totalCommission: number | null;
};

export type BookingVelocityRow = {
  date: string;
  bookingCount: number;
};

export type TopOfferByBookingsRow = {
  offerId: string;
  offerDisplayName: string;
  vendorName: string;
  departureDate: string;
  bookingCount: number;
  averageMarginPercent: number | null;
  totalCommission: number | null;
};

export type TopOfferByMarginRow = {
  offerId: string;
  offerDisplayName: string;
  vendorName: string;
  departureDate: string;
  bookingCount: number;
  averageMarginPercent: number;
  totalCommission: number | null;
};

export type InclusionTrend = {
  inclusionType: string;
  direction: 'up' | 'down' | 'stable';
  percentChange: number;
  reasoning: string;
  confidence: 'High' | 'Medium' | 'Low';
  expectedImpact: 'conversion up' | 'margin up' | 'both';
};

export type PredictedBestSeller = {
  inclusionType: string;
  reasoning: string;
  confidence: 'High' | 'Medium' | 'Low';
  expectedImpact: 'conversion up' | 'margin up' | 'both';
};

export type OfferTileOrderingRecommendation = {
  action: string;
  offerName: string;
  reasoning: string;
  confidence: 'High' | 'Medium' | 'Low';
  expectedImpact: 'conversion up' | 'margin up' | 'both';
};

export type DealPageRecommendation = {
  action: string;
  dealName: string;
  reasoning: string;
  confidence: 'High' | 'Medium' | 'Low';
  expectedImpact: 'conversion up' | 'margin up' | 'both';
};

export type DealStructuringRecommendation = {
  segment: string;
  recommendation: string;
  reasoning: string;
  confidence: 'High' | 'Medium' | 'Low';
  expectedImpact: 'conversion up' | 'margin up' | 'both';
};

export type OpportunityAndRiskSignal = {
  type: string;
  recommendation: string;
  reasoning: string;
  confidence: 'High' | 'Medium' | 'Low';
  expectedImpact: 'conversion up' | 'margin up' | 'both';
};

export type CompetitorGapVsLiveMerch = {
  competitorPush: string;
  ourGap: string;
  suggestion: string;
  confidence: 'High' | 'Medium' | 'Low';
  /** Cruise Guru campaign landing pages to review when mimicking this angle (from live crawl). */
  cruiseGuruCampaignUrlsToMimic: Array<string>;
};

export type DifferentiatedStrength = {
  point: string;
  confidence: 'High' | 'Medium' | 'Low';
};

export type CompetitorComparison = {
  summaryOneLiner: string;
  gapsVsOurLiveMerch: Array<CompetitorGapVsLiveMerch>;
  differentiatedStrengths: Array<DifferentiatedStrength>;
};

export type CruiseGuruCampaignHighlight = {
  title: string;
  pathSlug: string;
};

export type CruiseGuruPredictionsMeta = {
  crawlSucceeded: boolean;
  listingUrl: string;
  extractedCampaignCount: number;
  crawlErrorMessage?: string;
  topCampaignHighlights: Array<CruiseGuruCampaignHighlight>;
};

export type Predictions = {
  inclusionTrends: Array<InclusionTrend>;
  predictedBestSellers: Array<PredictedBestSeller>;
  offerTileOrderingRecommendations: Array<OfferTileOrderingRecommendation>;
  dealPageRecommendations: Array<DealPageRecommendation>;
  dealStructuringRecommendations: Array<DealStructuringRecommendation>;
  opportunityAndRiskSignals: Array<OpportunityAndRiskSignal>;
  competitorComparison: CompetitorComparison;
  cruiseGuru: CruiseGuruPredictionsMeta;
};

export type HealthReport = {
  inclusionBreakdown: Array<InclusionBreakdownRow>;
  inclusionPerformance: Array<InclusionPerformanceRow>;
  bookingVelocity: Array<BookingVelocityRow>;
  topByBookings: Array<TopOfferByBookingsRow>;
  topByMargin: Array<TopOfferByMarginRow>;
};

export type HealthReportQuery = {
  period: '30d' | '60d' | '90d';
  vendorId?: string;
};
export type RelationshipOffer = {
  id: string;
  name: string;
  status: string;
  cruiseType: string | null;
};

export type RelationshipDeparture = {
  id: string;
  offerId: string;
  departureDate: string;
  status: string;
  cruiseType: string | null;
  bookingCount: number;
};

export type RelationshipDepartureRate = {
  departureId: string;
  rateId: string;
  rateCode: string;
  isFlash: boolean;
  isNetRate: boolean;
  isEnabled: boolean;
};

export type RelationshipData = {
  offers: Array<RelationshipOffer>;
  departures: Array<RelationshipDeparture>;
  departureRates: Array<RelationshipDepartureRate>;
};

const executeQuery = async ({ query }: CruiseIqQueryRequest): Promise<CruiseIqQueryResult> => {
  const response = await cruisesAPI.http.post<{ result: CruiseIqQueryResult }>({
    resource: 'query',
    body: { query },
  });
  return response.result;
};

const getHealthReport = async (params: HealthReportQuery): Promise<HealthReport> => {
  const response = await cruisesAPI.http.get<HealthReport>({
    resource: 'cruise-iq/health',
    paginationParams: params as unknown as Record<string, unknown>,
  });
  return response.result;
};

const getRelationships = async (vendorId: string, offerIds?: Array<string>): Promise<RelationshipData> => {
  const queryParams: Record<string, string> = { vendorId };
  if (offerIds?.length) {
    queryParams.offerIds = offerIds.join(',');
  }

  const response = await cruisesAPI.http.get<RelationshipData>({
    resource: 'cruise-iq/relationships',
    paginationParams: queryParams,
  });
  return response.result;
};

const getHealthPredictions = async (params: HealthReportQuery): Promise<Predictions> => {
  const response = await cruisesAPI.http.get<Predictions>({
    resource: 'cruise-iq/health/predictions',
    paginationParams: params as unknown as Record<string, unknown>,
  });
  return response.result;
};

export default {
  executeQuery,
  getHealthReport,
  getRelationships,
  getHealthPredictions,
};
